import { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const phoneNumber = user?.phone
    ? user.phone.replace(/(\+\d{3})(\d{3})(\d{4})/, '$1 $2 $3')
    : null;

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        // On sign-in, claim any pending points (fire-and-forget)
        if (event === 'SIGNED_IN' && session?.access_token) {
          supabase.functions.invoke('claim-pending-points', {}).catch((e) => {
            console.warn('claim-pending-points failed (non-blocking):', e);
          });

          // Track referral if code was captured from URL
          const refCode = sessionStorage.getItem('flexaura_referral_code');
          if (refCode && session.user) {
            supabase.functions.invoke('referral', {
              body: { action: 'track', referralCode: refCode, newUserId: session.user.id },
            }).then(() => {
              sessionStorage.removeItem('flexaura_referral_code');
            }).catch((e) => {
              console.warn('referral tracking failed (non-blocking):', e);
            });
          }
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const sendOtp = useCallback(async (identifier: string, type: 'email' | 'phone') => {
    if (type === 'email') {
      const { error } = await supabase.auth.signInWithOtp({ email: identifier });
      return { error };
    } else {
      const { error } = await supabase.auth.signInWithOtp({ phone: identifier });
      return { error };
    }
  }, []);

  const verifyOtp = useCallback(async (identifier: string, token: string, type: 'email' | 'phone') => {
    const otpType = type === 'email' ? 'email' : 'sms';
    const params = type === 'email'
      ? { email: identifier, token, type: otpType as 'email' }
      : { phone: identifier, token, type: otpType as 'sms' };
    const { data, error } = await supabase.auth.verifyOtp(params);
    return { data, error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  // Legacy methods kept for backward compat (admin login)
  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email, password,
      options: { emailRedirectTo: `${window.location.origin}/` }
    });
    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    phoneNumber,
    sendOtp,
    verifyOtp,
    signIn,
    signUp,
    signOut
  };
};
